/**
 * F1 Service - Professional Formula 1 Data Service
 * API: API-Sports Formula 1
 * Architecture: Follows NFL service patterns with caching and error handling
 * Default Season: 2025
 */

class F1Service {
  constructor() {
    this.baseURL = 'https://v1.formula-1.api-sports.io';
    this.apiKey = process.env.REACT_APP_FOOTBALL_API_KEY; // Same API key for all API-Sports services
    this.debug = false;
    this.cache = {};
    this.defaultSeason = 2025;

    if (!this.apiKey) {
      console.warn('🏎️ F1 API key not found in environment variables');
    }
  }
 
  /**
   * Manual API key setter for runtime configuration
   */
  setApiKey(key) {
    this.apiKey = key;
    this.log('API key manually set:', { keyLength: key ? key.length : 0 });
  }

  /**
   * Debug logging utility
   */
  log(message, data = null) {
    if (this.debug) {
      console.log(`[F1 API] ${message}`, data || '');
    }
  }

  /**
   * Cache key generator
   */
  getCacheKey(endpoint, params) {
    return `f1_${endpoint}_${JSON.stringify(params)}`;
  }

  /**
   * Set cache with TTL
   */
  setCache(key, data, ttl = 300000) { // 5 minutes default
    this.cache[key] = {
      data,
      timestamp: Date.now(),
      ttl
    };
  }

  /**
   * Get cached data if not expired
   */
  getCache(key) {
    const cached = this.cache[key];
    if (!cached) return null;

    if (Date.now() - cached.timestamp > cached.ttl) {
      delete this.cache[key];
      return null;
    }

    return cached.data;
  }

  /**
   * Clear entire cache
   */
  clearCache() {
    this.cache = {};
    this.log('Cache cleared');
  }

  /**
   * Core API request handler with caching
   */
  async makeRequest(endpoint, params = {}) {
    // Check cache first
    const cacheKey = this.getCacheKey(endpoint, params);
    const cachedData = this.getCache(cacheKey);
    if (cachedData) {
      this.log(`Cache hit for ${endpoint}`, params);
      return cachedData;
    }

    this.log(`Making request to ${endpoint}`, params);

    if (!this.apiKey) {
      throw new Error(`F1 API key not configured.

Please configure the API key:
1. Add REACT_APP_F1_API_KEY to your .env file (same level as package.json)
2. Restart your development server after adding the variable
3. Or use F1Service.setApiKey('your_key_here') as a temporary workaround`);
    }

    const url = new URL(`${this.baseURL}${endpoint}`);

    // Add query parameters
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        url.searchParams.append(key, params[key]);
      }
    });

    this.log(`Request URL: ${url.toString()}`);

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'x-rapidapi-host': 'v1.formula-1.api-sports.io',
          'x-rapidapi-key': this.apiKey
        }
      });

      this.log(`Response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        this.log(`Error response:`, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      this.log(`Response data:`, data);

      // Check for API errors in response
      if (data.errors && data.errors.length > 0) {
        this.log(`API errors:`, data.errors);
        throw new Error(`API Error: ${data.errors.join(', ')}`);
      }

      // Cache successful responses with different TTLs based on data type
      let ttl = 300000; // 5 minutes default
      if (endpoint.includes('/races')) ttl = 60000; // 1 minute for race data (can be live)
      if (endpoint.includes('/rankings') || endpoint.includes('/standings')) ttl = 3600000; // 1 hour for standings
      if (endpoint.includes('/drivers') || endpoint.includes('/teams')) ttl = 86400000; // 24 hours for relatively static data
      if (endpoint.includes('/circuits')) ttl = 86400000 * 7; // 7 days for circuit data (very static)

      this.setCache(cacheKey, data, ttl);
      return data;
    } catch (error) {
      this.log(`Request failed:`, error.message);
      console.error('F1 API request failed:', error);
      throw error;
    }
  }

  // ==================== DRIVER ENDPOINTS ====================

  /**
   * Get F1 Drivers
   * @param {number|null} id - Driver ID (unique across all seasons)
   * @param {string|null} name - Driver full name (e.g., "Lewis Hamilton")
   * @param {string|null} search - Search for driver name (min 3 chars, e.g., "lewi")
   * @returns {Promise} Driver data with career stats, teams, championships
   *
   * Response includes:
   * - id, name, abbr, image, nationality, country
   * - birthdate, birthplace, number
   * - grands_prix_entered, world_championships, podiums
   * - highest_race_finish, highest_grid_position
   * - career_points
   * - teams[] - All teams by season
   */
  async getDrivers(id = null, name = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (name) params.name = name;
    if (search) params.search = search;

    // Endpoint requires at least one parameter
    if (!id && !name && !search) {
      throw new Error('getDrivers requires at least one parameter (id, name, or search)');
    }

    return await this.makeRequest('/drivers', params);
  }

  /**
   * Get all drivers for 2025 season
   * @returns {Promise} All current F1 drivers
   */
  async getCurrentDrivers() {
    // Get all drivers and filter by those who have 2025 season data
    // API search requires minimum 3 characters, so we'll search for common patterns
    // F1 has 20 drivers across 10 teams, need comprehensive search coverage
    try {
      const searchTerms = [
        // Top drivers
        'max', 'ver', 'lew', 'ham', 'fer', 'cha', 'lec', 'sai', 'nor', 'pia',
        // Mid-field
        'alo', 'str', 'per', 'rus', 'gas', 'oco', 'hul', 'mag',
        // Other drivers
        'tsu', 'ric', 'bot', 'zho', 'sar', 'alb', 'law', 'bea',
        // Common first names
        'oscar', 'pierre', 'esteban', 'kevin', 'nico', 'yuki', 'daniel',
        'valtteri', 'guanyu', 'logan', 'liam', 'alexander'
      ];
      const allDriversMap = new Map();

      // Search with different terms to get broad coverage
      for (const term of searchTerms) {
        try {
          const response = await this.getDrivers(null, null, term);
          if (response && response.response) {
            response.response.forEach(driver => {
              // Only add if not already in map (avoid duplicates)
              if (!allDriversMap.has(driver.id)) {
                // Check if driver has 2025 season data
                if (driver.teams && driver.teams.some(t => t.season === this.defaultSeason)) {
                  allDriversMap.set(driver.id, driver);
                }
              }
            });
          }
        } catch (err) {
          // Continue if one search fails
          this.log(`Search for '${term}' failed`, err.message);
        }
      }

      // Convert map to array
      const currentDrivers = Array.from(allDriversMap.values());

      return {
        get: 'drivers',
        parameters: { season: this.defaultSeason },
        errors: [],
        results: currentDrivers.length,
        response: currentDrivers
      };
    } catch (error) {
      this.log('Error fetching current drivers', error);
      throw error;
    }
  }

  /**
   * Get driver by ID with full career data
   * @param {number} id - Driver ID
   * @returns {Promise} Complete driver profile
   */
  async getDriverById(id) {
    return await this.getDrivers(id);
  }

  /**
   * Search drivers by name
   * @param {string} searchTerm - Name to search (min 3 characters)
   * @returns {Promise} Matching drivers
   */
  async searchDrivers(searchTerm) {
    if (!searchTerm || searchTerm.length < 3) {
      throw new Error('Search term must be at least 3 characters');
    }
    return await this.getDrivers(null, null, searchTerm);
  }

  // ==================== SEASON ENDPOINTS ====================

  /**
   * Get all available seasons
   * @returns {Promise} Array of season years (4-digit keys)
   *
   * Response: Array of integers [2025, 2024, 2023, ...]
   */
  async getSeasons() {
    return await this.makeRequest('/seasons');
  }

  // ==================== COMPETITION ENDPOINTS ====================

  /**
   * Get F1 Competitions (Grand Prix races)
   * @param {number|null} id - Competition ID (unique across all seasons)
   * @param {string|null} name - Competition name (e.g., "Australian Grand Prix")
   * @param {string|null} country - Country name (e.g., "Australia")
   * @param {string|null} city - City name (e.g., "Melbourne")
   * @param {string|null} search - Search competition name (min 3 chars)
   * @returns {Promise} Competition data with location
   *
   * Response includes:
   * - id, name
   * - location: { country, city }
   */
  async getCompetitions(id = null, name = null, country = null, city = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (name) params.name = name;
    if (country) params.country = country;
    if (city) params.city = city;
    if (search) params.search = search;

    // All parameters are optional - can fetch all competitions
    return await this.makeRequest('/competitions', params);
  }

  /**
   * Get all competitions (all Grand Prix races)
   * @returns {Promise} All available competitions
   */
  async getAllCompetitions() {
    return await this.getCompetitions();
  }

  /**
   * Get competition by ID
   * @param {number} id - Competition ID
   * @returns {Promise} Competition details
   */
  async getCompetitionById(id) {
    return await this.getCompetitions(id);
  }

  /**
   * Search competitions by name
   * @param {string} searchTerm - Name to search (min 3 characters)
   * @returns {Promise} Matching competitions
   */
  async searchCompetitions(searchTerm) {
    if (!searchTerm || searchTerm.length < 3) {
      throw new Error('Search term must be at least 3 characters');
    }
    return await this.getCompetitions(null, null, null, null, searchTerm);
  }

  // ==================== CIRCUIT ENDPOINTS ====================

  /**
   * Get F1 Circuits
   * @param {number|null} id - Circuit ID (unique across all seasons)
   * @param {number|null} competition - Competition ID to filter by
   * @param {string|null} name - Circuit name (e.g., "Melbourne Grand Prix Circuit")
   * @param {string|null} search - Search circuit name (min 3 chars)
   * @returns {Promise} Circuit data with track details and records
   *
   * Response includes:
   * - id, name, image
   * - competition: { id, name, location }
   * - first_grand_prix, laps, length, race_distance
   * - lap_record: { time, driver, year }
   * - capacity, opened, owner
   */
  async getCircuits(id = null, competition = null, name = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (competition) params.competition = competition;
    if (name) params.name = name;
    if (search) params.search = search;

    // All parameters are optional
    return await this.makeRequest('/circuits', params);
  }

  /**
   * Get all circuits
   * @returns {Promise} All F1 circuits
   */
  async getAllCircuits() {
    return await this.getCircuits();
  }

  /**
   * Get circuit by ID
   * @param {number} id - Circuit ID
   * @returns {Promise} Circuit details with lap records
   */
  async getCircuitById(id) {
    return await this.getCircuits(id);
  }

  /**
   * Get circuits for a specific competition
   * @param {number} competitionId - Competition ID
   * @returns {Promise} Circuits for the competition
   */
  async getCircuitsByCompetition(competitionId) {
    return await this.getCircuits(null, competitionId);
  }

  /**
   * Search circuits by name
   * @param {string} searchTerm - Name to search (min 3 characters)
   * @returns {Promise} Matching circuits
   */
  async searchCircuits(searchTerm) {
    if (!searchTerm || searchTerm.length < 3) {
      throw new Error('Search term must be at least 3 characters');
    }
    return await this.getCircuits(null, null, null, searchTerm);
  }

  // ==================== TEAM ENDPOINTS ====================

  /**
   * Get F1 Teams (Constructors)
   * @param {number|null} id - Team ID (unique across all seasons)
   * @param {string|null} name - Team name (e.g., "Red Bull Racing")
   * @param {string|null} search - Search team name (min 3 chars)
   * @returns {Promise} Team data with constructor details
   *
   * Response includes:
   * - id, name, logo, base
   * - first_team_entry, world_championships
   * - highest_race_finish: { position, number }
   * - pole_positions, fastest_laps
   * - president, director, technical_manager
   * - chassis, engine, tyres
   */
  async getTeams(id = null, name = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (name) params.name = name;
    if (search) params.search = search;

    // All parameters are optional
    return await this.makeRequest('/teams', params);
  }

  /**
   * Get all teams
   * @returns {Promise} All F1 constructor teams
   */
  async getAllTeams() {
    // Teams endpoint might require search parameter or return empty for future seasons
    // Try multiple approaches to get all teams
    try {
      // First try: no parameters (might work for current season)
      const response = await this.getTeams();
      if (response?.response?.length > 0) return response;

      // Second try: Search for common team names to get comprehensive coverage
      const searchTerms = ['red', 'fer', 'mec', 'mer', 'mcl', 'alpine', 'wil', 'has', 'alpha', 'aston'];
      const teamsMap = new Map();

      for (const term of searchTerms) {
        try {
          const teamResponse = await this.getTeams(null, null, term);
          if (teamResponse?.response) {
            teamResponse.response.forEach(team => {
              teamsMap.set(team.id, team);
            });
          }
        } catch (err) {
          this.log(`Team search for '${term}' failed`, err);
        }
      }

      const allTeams = Array.from(teamsMap.values());
      return {
        get: 'teams',
        errors: [],
        results: allTeams.length,
        response: allTeams
      };
    } catch (error) {
      this.log('Error fetching all teams', error);
      throw error;
    }
  }

  /**
   * Get team by ID
   * @param {number} id - Team ID
   * @returns {Promise} Complete team/constructor details
   */
  async getTeamById(id) {
    return await this.getTeams(id);
  }

  /**
   * Search teams by name
   * @param {string} searchTerm - Name to search (min 3 characters)
   * @returns {Promise} Matching teams
   */
  async searchTeams(searchTerm) {
    if (!searchTerm || searchTerm.length < 3) {
      throw new Error('Search term must be at least 3 characters');
    }
    return await this.getTeams(null, null, searchTerm);
  }

  // ==================== RACE ENDPOINTS ====================

  /**
   * Get F1 Races
   * Requires at least one parameter: id, date, next, last, or season
   *
   * @param {number|null} id - Race ID
   * @param {string|null} date - Date in YYYY-MM-DD format
   * @param {number|null} next - Get next X races (max 99)
   * @param {number|null} last - Get last X races (max 99)
   * @param {number|null} competition - Competition ID to filter by
   * @param {number|null} circuit - Circuit ID to filter by
   * @param {number|null} season - Season year (4 digits, defaults to 2025)
   * @param {string|null} type - Race type (Race, 1st Qualifying, 2nd Qualifying, 3rd Qualifying, Sprint, etc.)
   * @param {string|null} timezone - Timezone for date conversion (e.g., "Europe/London")
   * @returns {Promise} Race data with competition, circuit, laps, timing, status
   *
   * Response includes:
   * - id, season, type, status
   * - competition: { id, name, location }
   * - circuit: { id, name, image }
   * - laps: { current, total }
   * - fastest_lap: { driver: { id }, time }
   * - distance, timezone, date, weather
   *
   * Available Status: Live, Completed, Cancelled, Postponed, Scheduled
   * Available Types: Race, 1st Qualifying, 2nd Qualifying, 3rd Qualifying, Sprint,
   *                  1st Sprint Shootout, 2nd Sprint Shootout, 3rd Sprint Shootout,
   *                  1st Practice, 2nd Practice, 3rd Practice
   */
  async getRaces(
    id = null,
    date = null,
    next = null,
    last = null,
    competition = null,
    circuit = null,
    season = null,
    type = null,
    timezone = null
  ) {
    const params = {};
    if (id) params.id = id;
    if (date) params.date = date;
    if (next) params.next = next;
    if (last) params.last = last;
    if (competition) params.competition = competition;
    if (circuit) params.circuit = circuit;
    if (season) params.season = season;
    if (type) params.type = type;
    if (timezone) params.timezone = timezone;

    // Endpoint requires at least one of: id, date, next, last, or season
    if (!id && !date && !next && !last && !season) {
      throw new Error('getRaces requires at least one parameter: id, date, next, last, or season');
    }

    return await this.makeRequest('/races', params);
  }

  /**
   * Get race by ID
   * @param {number} id - Race ID
   * @param {string|null} timezone - Timezone for date conversion
   * @returns {Promise} Race details
   */
  async getRaceById(id, timezone = null) {
    return await this.getRaces(id, null, null, null, null, null, null, null, timezone);
  }

  /**
   * Get races for current season (2025)
   * @param {string|null} type - Filter by race type
   * @param {string|null} timezone - Timezone for dates
   * @returns {Promise} All races for 2025 season
   */
  async getCurrentSeasonRaces(type = null, timezone = null) {
    return await this.getRaces(
      null,
      null,
      null,
      null,
      null,
      null,
      this.defaultSeason,
      type,
      timezone
    );
  }

  /**
   * Get races for specific season
   * @param {number} season - Season year
   * @param {string|null} type - Filter by race type
   * @param {string|null} timezone - Timezone for dates
   * @returns {Promise} All races for the season
   */
  async getRacesBySeason(season, type = null, timezone = null) {
    return await this.getRaces(null, null, null, null, null, null, season, type, timezone);
  }

  /**
   * Get upcoming races
   * @param {number} count - Number of upcoming races (max 99)
   * @param {string|null} timezone - Timezone for dates
   * @returns {Promise} Next X races
   */
  async getUpcomingRaces(count = 5, timezone = null) {
    if (count > 99) count = 99;
    return await this.getRaces(null, null, count, null, null, null, null, null, timezone);
  }

  /**
   * Get recent races
   * @param {number} count - Number of recent races (max 99)
   * @param {string|null} timezone - Timezone for dates
   * @returns {Promise} Last X races
   */
  async getRecentRaces(count = 5, timezone = null) {
    if (count > 99) count = 99;
    return await this.getRaces(null, null, null, count, null, null, null, null, timezone);
  }

  /**
   * Get races by date
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {string|null} timezone - Timezone for date conversion
   * @returns {Promise} Races on specific date
   */
  async getRacesByDate(date, timezone = null) {
    return await this.getRaces(null, date, null, null, null, null, null, null, timezone);
  }

  /**
   * Get races for a specific competition
   * @param {number} competitionId - Competition ID (Grand Prix)
   * @param {number|null} season - Season year (defaults to 2025)
   * @param {string|null} timezone - Timezone for dates
   * @returns {Promise} Races for the competition
   */
  async getRacesByCompetition(competitionId, season = null, timezone = null) {
    return await this.getRaces(
      null,
      null,
      null,
      null,
      competitionId,
      null,
      season || this.defaultSeason,
      null,
      timezone
    );
  }

  /**
   * Get races at a specific circuit
   * @param {number} circuitId - Circuit ID
   * @param {number|null} season - Season year (defaults to 2025)
   * @param {string|null} timezone - Timezone for dates
   * @returns {Promise} Races at the circuit
   */
  async getRacesByCircuit(circuitId, season = null, timezone = null) {
    return await this.getRaces(
      null,
      null,
      null,
      null,
      null,
      circuitId,
      season || this.defaultSeason,
      null,
      timezone
    );
  }

  /**
   * Get live races
   * Get all currently running races/sessions
   * @param {string|null} timezone - Timezone for dates
   * @returns {Promise} Currently live races
   */
  async getLiveRaces(timezone = null) {
    // Check both recent and upcoming races to catch live races
    // A race can be "live" if it's in progress today
    try {
      const today = new Date().toISOString().split('T')[0];

      // Get races from the current season
      const allRaces = await this.getCurrentSeasonRaces(null, timezone);

      if (allRaces && allRaces.response) {
        // Filter for races that are:
        // 1. Explicitly marked as "Live" by API
        // 2. Happening today (any session type)
        const liveRaces = allRaces.response.filter(race => {
          const raceDate = race.date ? race.date.split('T')[0] : '';
          return (
            race.status === 'Live' ||
            (raceDate === today && race.type === 'Race')
          );
        });

        return {
          ...allRaces,
          response: liveRaces
        };
      }

      return allRaces;
    } catch (error) {
      this.log('Error fetching live races', error);
      throw error;
    }
  }

  /**
   * Get completed races for a season
   * @param {number|null} season - Season year (defaults to 2025)
   * @param {string|null} timezone - Timezone for dates
   * @returns {Promise} Completed races
   */
  async getCompletedRaces(season = null, timezone = null) {
    const response = await this.getCurrentSeasonRaces(null, timezone);
    if (response && response.response) {
      // Filter for completed races only (main race type)
      return {
        ...response,
        response: response.response.filter(race =>
          race.status === 'Completed' && race.type === 'Race'
        )
      };
    }
    return response;
  }

  /**
   * Get qualifying sessions for current season
   * @param {string|null} timezone - Timezone for dates
   * @returns {Promise} Qualifying sessions
   */
  async getQualifyingSessions(season = null, timezone = null) {
    // Get all qualifying types
    const types = ['1st Qualifying', '2nd Qualifying', '3rd Qualifying'];
    const allQualifying = [];

    for (const type of types) {
      const response = await this.getRaces(
        null,
        null,
        null,
        null,
        null,
        null,
        season || this.defaultSeason,
        type,
        timezone
      );
      if (response && response.response) {
        allQualifying.push(...response.response);
      }
    }

    return {
      get: 'races',
      parameters: { season: season || this.defaultSeason, types: types },
      results: allQualifying.length,
      response: allQualifying
    };
  }

  /**
   * Get practice sessions for current season
   * @param {string|null} timezone - Timezone for dates
   * @returns {Promise} Practice sessions
   */
  async getPracticeSessions(season = null, timezone = null) {
    const types = ['1st Practice', '2nd Practice', '3rd Practice'];
    const allPractice = [];

    for (const type of types) {
      const response = await this.getRaces(
        null,
        null,
        null,
        null,
        null,
        null,
        season || this.defaultSeason,
        type,
        timezone
      );
      if (response && response.response) {
        allPractice.push(...response.response);
      }
    }

    return {
      get: 'races',
      parameters: { season: season || this.defaultSeason, types: types },
      results: allPractice.length,
      response: allPractice
    };
  }

  // ==================== RANKINGS ENDPOINTS ====================

  /**
   * Get F1 Rankings/Standings
   * This is a multi-purpose endpoint that returns different data based on parameters
   *
   * @param {number|null} driver - Driver ID for driver-specific rankings
   * @param {number|null} team - Team ID for team-specific rankings
   * @param {number|null} race - Race ID for race results, fastest laps, starting grid
   * @param {number|null} season - Season year (defaults to 2025)
   * @returns {Promise} Rankings data
   *
   * Response varies by parameters:
   * - season only: Constructor standings
   * - season + driver: Driver standings
   * - race only: Race results
   * - Use helper methods for specific ranking types
   */
  async getRankings(driver = null, team = null, race = null, season = null) {
    const params = {};
    if (driver) params.driver = driver;
    if (team) params.team = team;
    if (race) params.race = race;
    if (season) params.season = season;

    // Requires at least one parameter
    if (!driver && !team && !race && !season) {
      throw new Error('getRankings requires at least one parameter');
    }

    return await this.makeRequest('/rankings', params);
  }

  /**
   * Get Driver Championship Standings
   * @param {number|null} season - Season year (defaults to 2025)
   * @returns {Promise} Driver standings with points, wins, gap to leader
   *
   * Response includes:
   * - position, points, wins, behind
   * - driver: { id, name, abbr, number, image }
   * - team: { id, name, logo }
   * - season
   */
  async getDriverStandings(season = null) {
    // Note: API returns constructor standings by default with just season
    // We need to query with a driver parameter to get driver standings
    // Let's fetch all drivers first, then get their standings
    try {
      const response = await this.makeRequest('/rankings/drivers', {
        season: season || this.defaultSeason
      });
      return response;
    } catch (error) {
      // Fallback: Try without /drivers path
      this.log('Driver standings fetch failed, trying alternate method', error);
      return await this.getRankings(null, null, null, season || this.defaultSeason);
    }
  }

  /**
   * Get Constructor Championship Standings
   * @param {number|null} season - Season year (defaults to 2025)
   * @returns {Promise} Constructor/team standings
   *
   * Response includes:
   * - position, points
   * - team: { id, name, logo }
   * - season
   */
  async getConstructorStandings(season = null) {
    try {
      // Try the teams endpoint path first
      const response = await this.makeRequest('/rankings/teams', {
        season: season || this.defaultSeason
      });
      return response;
    } catch (error) {
      this.log('Constructor standings fetch failed, trying alternate method', error);
      // Fallback to general rankings endpoint
      return await this.getRankings(null, null, null, season || this.defaultSeason);
    }
  }

  /**
   * Get Race Results (finishing positions)
   * @param {number} raceId - Race ID
   * @returns {Promise} Race results with finishing order
   *
   * Response includes:
   * - race: { id }
   * - position, time, laps, grid, pits, gap
   * - driver: { id, name, abbr, number, image }
   * - team: { id, name, logo }
   */
  async getRaceResults(raceId) {
    if (!raceId) {
      throw new Error('getRaceResults requires a race ID');
    }
    return await this.makeRequest('/rankings/races', { race: raceId });
  }

  /**
   * Get Fastest Laps for a race
   * @param {number} raceId - Race ID
   * @returns {Promise} Fastest lap rankings
   *
   * Response includes:
   * - race: { id }
   * - position, lap, time, avg_speed
   * - driver: { id, name, abbr, number, image }
   * - team: { id, name, logo }
   */
  async getFastestLaps(raceId) {
    if (!raceId) {
      throw new Error('getFastestLaps requires a race ID');
    }
    return await this.makeRequest('/rankings/fastest-laps', { race: raceId });
  }

  /**
   * Get Starting Grid (qualifying results)
   * @param {number} raceId - Race ID
   * @returns {Promise} Starting grid positions
   *
   * Response includes:
   * - race: { id }
   * - position, time
   * - driver: { id, name, abbr, number, image }
   * - team: { id, name, logo }
   */
  async getStartingGrid(raceId) {
    if (!raceId) {
      throw new Error('getStartingGrid requires a race ID');
    }
    return await this.makeRequest('/rankings/starting-grid', { race: raceId });
  }

  /**
   * Get current season driver standings (2025)
   * @returns {Promise} Current driver standings
   */
  async getCurrentDriverStandings() {
    return await this.getDriverStandings(this.defaultSeason);
  }

  /**
   * Get current season constructor standings (2025)
   * @returns {Promise} Current constructor standings
   */
  async getCurrentConstructorStandings() {
    return await this.getConstructorStandings(this.defaultSeason);
  }

  /**
   * Get latest race results
   * Fetches the most recent completed race and its results
   * @returns {Promise} Latest race results
   */
  async getLatestRaceResults() {
    try {
      // Get recent races
      const recentRaces = await this.getRecentRaces(5);
      if (recentRaces && recentRaces.response && recentRaces.response.length > 0) {
        // Find first completed race
        const completedRace = recentRaces.response.find(r => r.status === 'Completed');
        if (completedRace) {
          return await this.getRaceResults(completedRace.id);
        }
      }
      return null;
    } catch (error) {
      this.log('Error fetching latest race results', error);
      throw error;
    }
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Transform driver data for frontend consumption
   * Normalizes API response to match our component's data structure
   */
  transformDriverData(apiDriver) {
    if (!apiDriver) return null;

    // Get current team (2025 season)
    const currentTeam = apiDriver.teams?.find(t => t.season === this.defaultSeason);

    return {
      id: apiDriver.id,
      firstName: apiDriver.name?.split(' ').slice(0, -1).join(' ') || '',
      lastName: apiDriver.name?.split(' ').slice(-1)[0] || apiDriver.name,
      fullName: apiDriver.name,
      number: apiDriver.number,
      abbr: apiDriver.abbr,
      image: apiDriver.image,
      team: currentTeam?.team?.name || 'Unknown',
      teamId: currentTeam?.team?.id,
      teamLogo: currentTeam?.team?.logo,
      teamCode: this.getTeamCode(currentTeam?.team?.name),
      country: apiDriver.country?.code || 'UNK',
      nationality: apiDriver.nationality,
      birthdate: apiDriver.birthdate,
      birthplace: apiDriver.birthplace,
      // Career stats
      stats: {
        grandsPrixEntered: apiDriver.grands_prix_entered,
        worldChampionships: apiDriver.world_championships,
        podiums: apiDriver.podiums,
        highestRaceFinish: apiDriver.highest_race_finish?.number || apiDriver.highest_race_finish,
        highestGridPosition: apiDriver.highest_grid_position,
        careerPoints: parseFloat(apiDriver.career_points) || 0
      },
      teams: apiDriver.teams || []
    };
  }

  /**
   * Get team code from team name
   * Maps full team names to 3-letter codes for display
   */
  getTeamCode(teamName) {
    const teamCodes = {
      // McLaren
      'McLaren': 'MCL',
      'McLaren Racing': 'MCL',

      // Ferrari
      'Ferrari': 'FER',
      'Scuderia Ferrari': 'FER',

      // Red Bull
      'Red Bull Racing': 'RBR',

      // Mercedes
      'Mercedes-AMG Petronas': 'MER',
      'Mercedes': 'MER',

      // Aston Martin
      'Aston Martin': 'AST',
      'Aston Martin F1 Team': 'AST',

      // Alpine
      'Alpine': 'ALP',
      'Alpine F1 Team': 'ALP',

      // Williams
      'Williams': 'WIL',
      'Williams Racing': 'WIL',

      // Racing Bulls
      'RB': 'RB',
      'Racing Bulls': 'RAC',

      // Haas
      'Haas F1 Team': 'HAS',
      'Haas': 'HAS',

      // Sauber
      'Kick Sauber': 'SAU',
      'Sauber': 'SAU'
    };
    return teamCodes[teamName] || teamName?.substring(0, 3).toUpperCase() || 'UNK';
  }

  /**
   * Get team color from team name
   * Returns primary and accent colors for team branding
   */
  getTeamColors(teamName) {
    const teamColors = {
      // McLaren
      'McLaren': { primary: '#FF8700', accent: '#47C7FC', text: '#FFFFFF' },
      'McLaren Racing': { primary: '#FF8700', accent: '#47C7FC', text: '#FFFFFF' },

      // Ferrari
      'Ferrari': { primary: '#E8002D', accent: '#FFF500', text: '#FFFFFF' },
      'Scuderia Ferrari': { primary: '#E8002D', accent: '#FFF500', text: '#FFFFFF' },

      // Red Bull
      'Red Bull Racing': { primary: '#3671C6', accent: '#FCD700', text: '#FFFFFF' },

      // Mercedes
      'Mercedes-AMG Petronas': { primary: '#27F4D2', accent: '#6CD3BF', text: '#000000' },
      'Mercedes': { primary: '#27F4D2', accent: '#6CD3BF', text: '#000000' },

      // Aston Martin
      'Aston Martin': { primary: '#229971', accent: '#CEDC00', text: '#FFFFFF' },
      'Aston Martin F1 Team': { primary: '#229971', accent: '#CEDC00', text: '#FFFFFF' },

      // Alpine
      'Alpine': { primary: '#FF87BC', accent: '#2293D1', text: '#FFFFFF' },
      'Alpine F1 Team': { primary: '#FF87BC', accent: '#2293D1', text: '#FFFFFF' },

      // Williams
      'Williams': { primary: '#64C4FF', accent: '#041E42', text: '#FFFFFF' },
      'Williams Racing': { primary: '#64C4FF', accent: '#041E42', text: '#FFFFFF' },

      // Racing Bulls (RB)
      'RB': { primary: '#6692FF', accent: '#1E3A5F', text: '#FFFFFF' },
      'Racing Bulls': { primary: '#6692FF', accent: '#1E3A5F', text: '#FFFFFF' },

      // Haas
      'Haas F1 Team': { primary: '#B6BABD', accent: '#ED1C24', text: '#000000' },
      'Haas': { primary: '#B6BABD', accent: '#ED1C24', text: '#000000' },

      // Sauber/Kick Sauber
      'Kick Sauber': { primary: '#52E252', accent: '#00E800', text: '#000000' },
      'Sauber': { primary: '#52E252', accent: '#00E800', text: '#000000' }
    };
    return teamColors[teamName] || { primary: '#CCCCCC', accent: '#999999', text: '#000000' };
  }

  /**
   * Get F1.com official car image URL for a team
   * Returns high-resolution car images from Formula1.com CDN
   * @param {string} teamName - Team name (e.g., "Ferrari", "Red Bull Racing")
   * @param {number} season - Season year (default: 2025)
   * @returns {string} Car image URL or null if not found
   */
  getTeamCarImage(teamName, season = 2025) {
    // Map team names to F1.com URL slugs
    const teamSlugs = {
      'McLaren': 'mclaren',
      'McLaren Racing': 'mclaren',
      'Ferrari': 'ferrari',
      'Scuderia Ferrari': 'ferrari',
      'Red Bull Racing': 'redbullracing',
      'Red Bull': 'redbullracing',
      'Mercedes-AMG Petronas': 'mercedes',
      'Mercedes': 'mercedes',
      'Aston Martin': 'astonmartin',
      'Aston Martin F1 Team': 'astonmartin',
      'Alpine': 'alpine',
      'Alpine F1 Team': 'alpine',
      'Williams': 'williams',
      'Williams Racing': 'williams',
      'RB': 'racingbulls',
      'Racing Bulls': 'racingbulls',
      'Haas F1 Team': 'haas',
      'Haas': 'haas',
      'Kick Sauber': 'sauber',
      'Sauber': 'sauber'
    };

    const slug = teamSlugs[teamName];
    if (!slug) return null;

    // F1.com CDN URL pattern
    // Format: https://media.formula1.com/image/upload/c_lfill,w_3392/q_auto/v1740000000/common/f1/{year}/{slug}/{year}{slug}carright.webp
    return `https://media.formula1.com/image/upload/c_lfill,w_1200/q_auto/v1740000000/common/f1/${season}/${slug}/${season}${slug}carright.webp`;
  }

  /**
   * Get default season (2025)
   */
  getDefaultSeason() {
    return this.defaultSeason;
  }

  /**
   * Get current season (same as default for now)
   */
  getCurrentSeason() {
    return this.defaultSeason;
  }

  /**
   * Get available seasons for F1
   */
  getAvailableSeasons() {
    // F1 seasons available in API
    const currentYear = new Date().getFullYear();
    const seasons = [];
    for (let year = currentYear; year >= 1950; year--) {
      seasons.push(year);
    }
    return seasons;
  }
}

// Create and export singleton instance
const f1Service = new F1Service();
export default f1Service;